import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../../../fetchSetup";
import { fetchUserFromRedis } from "../../AuthonticationScreens/Register";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const PRESCRIPTION_LIST_API = process.env.REACT_APP_PRESCRITON_BASE_API_URL;


const normalizePrescription = (item, index) => {
  if (typeof item === "string") {
    return {
      id: `prescription-${index}`,
      title: item.split("/").pop(),
      url: item,
    };
  }

  if (item && typeof item === "object") {
    const url =
      item.cloudfront_url ||
      item.cloudfrontUrl ||
      item.url ||
      item.file_url ||
      item.link ||
      "";

    return {
      id: item.booking_id || item._id || `prescription-${index}`,
      title:
        item.file_name ||
        item.original_filename ||
        item.filename ||
        item.name ||
        "Prescription",
      url,
      doctor: item.doctor_name,
      date: item.date,
    };
  }

  return {
    id: `prescription-${index}`,
    title: "Prescription",
    url: "",
  };
};

function getFileIcon(name) {
  const ext = (name.split(".").pop() || "").toLowerCase();

  if (ext === "pdf") {
    return (
      <PictureAsPdfOutlinedIcon
        sx={{ fontSize: 42, color: "#D32F2F" }}
      />
    );
  }

  return (
    <InsertDriveFileOutlinedIcon
      sx={{ fontSize: 42, color: "#1976D2" }}
    />
  );
}

const PrescriptionsView = () => {
  const navigate = useNavigate();

  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    loadPrescriptions();
  }, []);

  const loadPrescriptions = async () => {
    try {
      setLoading(true);
      setError("");

      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");

      if (!userId || !token) {
        setError("Please log in to view prescriptions.");
        return;
      }

      const user = await fetchUserFromRedis(userId, token);

      const email = user?.email || "";
      setUserEmail(email);

      if (!email) {
        setError("Email not found.");
        return;
      }
      

      try {
            const res = await apiRequest(`${PRESCRIPTION_LIST_API}/prescriptions/all-list/${user.email}`);
            const data = await res.json();
            if (data.success && data.files) {
              setPrescriptions(
                data.files.map((item, index) =>
                normalizePrescription(item, index)
                )
            );
              return data.files;
            }
            setPrescriptions([]);
            return [];
          } catch (error) {
            setPrescriptions([]);
            return [];
          }
      
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (url, title) => {
    if (!url) return;

    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = title;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const hasPrescriptions = useMemo(
    () => prescriptions.some((item) => Boolean(item.url)),
    [prescriptions]
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 4 },
          borderRadius: 4,
          border: "1px solid #ECECEC",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
            >
              Prescriptions
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {userEmail
                ? `Showing prescriptions for ${userEmail}`
                : "View and download your prescriptions."}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            onClick={() => navigate("/profile-dashboard")}
            sx={{
              borderColor: "#006D77",
              color: "#006D77",
              textTransform: "none",
            }}
          >
            Back to Profile
          </Button>
        </Box>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 8,
            }}
          >
            <CircularProgress sx={{ color: "#006D77" }} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <DescriptionOutlinedIcon
              sx={{ fontSize: 48, color: "#9CA3AF" }}
            />

            <Typography
              variant="h6"
              sx={{ mt: 1 }}
            >
              {error}
            </Typography>
          </Box>
        ) : !hasPrescriptions ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <DescriptionOutlinedIcon
              sx={{ fontSize: 48, color: "#9CA3AF" }}
            />

            <Typography
              variant="h6"
              sx={{ mt: 1 }}
            >
              No prescriptions found.
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              Your uploaded prescriptions will appear here.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {prescriptions
              .filter((item) => item.url)
              .map((prescription) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={4}
                  key={prescription.id}
                >
                  <Card
                    elevation={0}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: 3,
                      height: "100%",
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          alignItems: "center",
                        }}
                      >
                        {getFileIcon(prescription.title)}

                        <Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                          >
                            {prescription.title}
                          </Typography>

                          <Chip
                            label={prescription.id}
                            size="small"
                            sx={{
                              mt: 1,
                              bgcolor: "#E0F2F1",
                              color: "#006D77",
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </Box>

                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 2 }}
                      >
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<DownloadOutlinedIcon />}
                          onClick={() =>
                            handleDownload(
                              prescription.url,
                              prescription.title
                            )
                          }
                          sx={{
                            textTransform: "none",
                            bgcolor: "#006D77",
                            "&:hover": {
                              bgcolor: "#005761",
                            },
                          }}
                        >
                          Download
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        )}
      </Paper>
    </Container>
  );
};

export default PrescriptionsView;